import { Song } from '../types/song';
import { config } from '../config/environment';

interface ImportResult {
  title: string;
  artist: string;
  key: string;
  chordPro: string;
  tempo?: number;
  capo?: number;
}

interface AsyncImportResult {
  jobId: string;
  message: string;
  status: 'processing';
}

// Type guard to check if result is async
export const isAsyncImportResult = (result: ImportResult | AsyncImportResult): result is AsyncImportResult => {
  return 'jobId' in result && result.status === 'processing';
}

interface JobStatusResult {
  jobId: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  result?: {
    chordPro: string;
    metadata: {
      title: string;
      artist: string;
      key?: string;
      capo?: number;
      tempo?: number;
      [key: string]: any;
    };
  };
  error?: string;
  createdAt: string;
  updatedAt: string;
}

// Parse ChordPro directives locally
export const parseChordProDirectives = (content: string) => {
  const titleMatch = content.match(/\{title:\s*([^}]+)\}/i);
  const artistMatch = content.match(/\{artist:\s*([^}]+)\}/i);
  const keyMatch = content.match(/\{key:\s*([^}]+)\}/i);
  const tempoMatch = content.match(/\{tempo:\s*([^}]+)\}/i);
  const capoMatch = content.match(/\{capo:\s*([^}]+)\}/i);

  return {
    title: titleMatch?.[1]?.trim() || 'Untitled',
    artist: artistMatch?.[1]?.trim() || 'Unknown Artist',
    key: keyMatch?.[1]?.trim() || 'C',
    tempo: tempoMatch ? parseInt(tempoMatch[1]) : undefined,
    capo: capoMatch ? parseInt(capoMatch[1]) : undefined
  };
};

// Validate ChordPro content
const validateChordProContent = (content: string): void => {
  if (!content || content.trim() === '') {
    throw new Error('Please provide ChordPro content to import');
  }
  
  // Check for basic ChordPro structure
  const hasChordDirectives = /\{[^}]+\}/.test(content);
  const hasChordBrackets = /\[[^\]]+\]/.test(content);
  const hasLyrics = /[a-zA-Z]+/.test(content);
  
  if (!hasLyrics) {
    throw new Error('ChordPro content must contain lyrics');
  }
  
  // Warn if no chords or directives found
  if (!hasChordDirectives && !hasChordBrackets) {
    console.warn('No ChordPro directives or chord brackets found in content');
  }
};

// Import ChordPro content
export const importChordPro = async (
  content: string,
  metadata: Partial<Song> = {}
): Promise<ImportResult> => {
  validateChordProContent(content);

  // Try backend service first if available
  if (config.apiUrl && !config.apiUrl.includes('localhost')) {
    try {
      const response = await fetch(`${config.apiUrl}/songs/convert`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'convert',
          content: content,
          format: 'chordpro'
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to convert song: ${response.statusText}`);
      }

      const result = await response.json();
      
      return {
        title: result.title || metadata.title || 'Untitled',
        artist: result.artist || metadata.artist || 'Unknown Artist',
        key: result.key || metadata.key || 'C',
        chordPro: result.chordProContent || content,
        tempo: result.tempo || metadata.tempo,
        capo: result.capo
      };
    } catch (error) {
      console.warn('Backend service failed, using local parsing:', error);
      // Fall through to local parsing
    }
  }

  // Fallback to local parsing
  const parsed = parseChordProDirectives(content);
  
  return {
    title: parsed.title || metadata.title || 'Untitled',
    artist: parsed.artist || metadata.artist || 'Unknown Artist',
    key: parsed.key || metadata.key || 'C',
    chordPro: content,
    tempo: parsed.tempo || metadata.tempo,
    capo: parsed.capo
  };
};

// Validate Ultimate Guitar content
const validateUltimateGuitarContent = (content: string): void => {
  if (!content || content.trim() === '') {
    throw new Error('Please provide tab content to import');
  }
  
  // Check for basic tab structure - should have chords in brackets
  const hasChordBrackets = /\[[^\]]+\]/.test(content);
  const hasLyrics = /[a-zA-Z]+/.test(content);
  
  if (!hasLyrics) {
    throw new Error('Tab content must contain lyrics');
  }
  
  if (!hasChordBrackets) {
    throw new Error('Tab content must contain chords in square brackets (e.g., [G], [C], [Am])');
  }
};

// Import from Ultimate Guitar format (now async)
export const importFromUltimateGuitar = async (
  content: string,
  metadata: Partial<Song> = {}
): Promise<ImportResult> => {
  validateUltimateGuitarContent(content);
  
  // Ultimate Guitar import requires backend service
  if (!config.apiUrl || config.apiUrl.includes('localhost')) {
    throw new Error('Backend service not available. Please use ChordPro import instead.');
  }

  const response = await fetch(`${config.apiUrl}/songs/convert`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      action: 'convert',
      content: content,
      format: 'ultimate-guitar'
    })
  });

  if (!response.ok) {
    throw new Error(`Failed to convert song: ${response.statusText}`);
  }

  const result = await response.json();
  
  return {
    title: result.title || metadata.title || 'Untitled',
    artist: result.artist || metadata.artist || 'Unknown Artist',
    key: result.key || metadata.key || 'C',
    chordPro: result.chordProContent || content,
    tempo: result.tempo || metadata.tempo,
    capo: result.capo
  };
};

// Validate URL
const validateUrl = (url: string): void => {
  if (!url || typeof url !== 'string') {
    throw new Error('Please provide a valid URL');
  }
  
  if (!url.startsWith('http://') && !url.startsWith('https://')) {
    throw new Error('URL must start with http:// or https://');
  }
  
  try {
    new URL(url);
  } catch (error) {
    throw new Error('Invalid URL format');
  }
  
  // Check for supported domains
  const supportedDomains = ['ultimate-guitar.com', 'tabs.ultimate-guitar.com'];
  const urlObj = new URL(url);
  const isSupported = supportedDomains.some(domain => urlObj.hostname.includes(domain));
  
  if (!isSupported) {
    console.warn(`URL domain ${urlObj.hostname} may not be fully supported`);
  }
};

// Import from URL (now async with job tracking)
export const importFromUrl = async (url: string, useAsync = true): Promise<ImportResult | AsyncImportResult> => {
  validateUrl(url);
  
  if (!config.apiUrl) {
    console.error('Import Service Error - API URL not configured', {
      apiUrl: config.apiUrl,
      environment: config.environment,
      isDevelopment: config.isDevelopment,
      isProduction: config.isProduction
    });
    throw new Error('Backend service not available - API URL not configured');
  }
  
  // In development, warn but allow localhost
  if (config.isDevelopment && config.apiUrl.includes('localhost')) {
    console.warn('Using localhost API for URL import in development mode');
  }

  const response = await fetch(`${config.apiUrl}/fetch`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ url, async: useAsync })
  });

  const result = await response.json();
  
  // Debug logging
  console.log('Import response:', {
    status: response.status,
    hasChordProContent: !!result.chordProContent,
    hasContent: !!result.content,
    chordProPreview: result.chordProContent?.substring(0, 100),
    contentPreview: result.content?.substring(0, 100),
    fullResult: result
  });
  
  // Check if we got instructions instead of content
  if (result.instructions && result.hint) {
    throw new Error(`URL fetch returned instructions: ${result.instructions}`);
  }

  // If async mode, return job info
  if (response.status === 202 && result.jobId) {
    return {
      jobId: result.jobId,
      message: result.message,
      status: 'processing'
    } as AsyncImportResult;
  }

  if (!response.ok) {
    throw new Error(result.error || 'Failed to import from URL');
  }

  // For synchronous mode, we need to convert the content
  if (result.content && !result.chordProContent) {
    // The URL fetcher returned raw content, we need to convert it
    // Since Ultimate Guitar conversion requires backend processing, we should use async mode
    throw new Error('URL import requires async mode for Ultimate Guitar tabs. Please try again.');
  }

  if (!result.chordProContent) {
    throw new Error('No chord content returned from URL');
  }

  const importResult = {
    title: result.title || 'Untitled',
    artist: result.artist || 'Unknown Artist',
    key: result.key || 'C',
    chordPro: result.chordProContent,
    tempo: result.tempo,
    capo: result.capo
  };
  
  console.log('Returning import result:', {
    title: importResult.title,
    hasChordPro: !!importResult.chordPro,
    chordProPreview: importResult.chordPro?.substring(0, 100)
  });
  
  return importResult;
};

// Check job status
export const checkJobStatus = async (jobId: string): Promise<JobStatusResult> => {
  if (!config.apiUrl || config.apiUrl.includes('localhost')) {
    throw new Error('Backend service not available');
  }

  const response = await fetch(`${config.apiUrl}/jobs/${jobId}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    }
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to check job status');
  }

  try {
    const result = await response.json();
    return result as JobStatusResult;
  } catch (err) {
    console.error('Failed to parse job status response:', err);
    throw new Error('Invalid response format from server');
  }
};

// Poll for job completion
export const pollJobStatus = async (
  jobId: string,
  onProgress?: (status: JobStatusResult) => void,
  maxAttempts = 30,
  intervalMs = 2000
): Promise<JobStatusResult> => {
  let attempts = 0;
  
  while (attempts < maxAttempts) {
    try {
      const status = await checkJobStatus(jobId);
      
      if (onProgress) {
        onProgress(status);
      }
      
      if (status.status === 'completed' || status.status === 'failed') {
        return status;
      }
      
      // Wait before next attempt
      await new Promise(resolve => setTimeout(resolve, intervalMs));
      attempts++;
    } catch (error) {
      console.error('Error checking job status:', error);
      attempts++;
      
      if (attempts >= maxAttempts) {
        throw error;
      }
      
      // Wait before retry
      await new Promise(resolve => setTimeout(resolve, intervalMs));
    }
  }
  
  throw new Error('Job status check timed out');
};

// Export/Import data format
export interface ExportData {
  songs: Song[];
  setlists?: any[];
  exportedAt: string;
  version: string;
}

// Export songs to JSON with version
export const exportSongs = (songs: Song[], setlists: any[] = []): string => {
  const exportData: ExportData = {
    songs,
    setlists,
    exportedAt: new Date().toISOString(),
    version: '1.0' // Version for future migration compatibility
  };
  return JSON.stringify(exportData, null, 2);
};

// Validate song data
const validateSong = (song: any): song is Song => {
  return (
    typeof song === 'object' &&
    typeof song.id === 'string' &&
    typeof song.title === 'string' &&
    typeof song.artist === 'string' &&
    typeof song.chordProContent === 'string' &&
    (!song.key || typeof song.key === 'string') &&
    (!song.originalKey || typeof song.originalKey === 'string') &&
    (!song.tags || Array.isArray(song.tags)) &&
    (!song.tempo || typeof song.tempo === 'number') &&
    (!song.capo || typeof song.capo === 'number')
  );
};

// Validate setlist data
const validateSetlist = (setlist: any): boolean => {
  return (
    typeof setlist === 'object' &&
    typeof setlist.id === 'string' &&
    typeof setlist.name === 'string' &&
    Array.isArray(setlist.songs) &&
    setlist.songs.every((id: any) => typeof id === 'string')
  );
};

// Import songs from JSON
export const importSongsFromJson = (data: string): ExportData => {
  let parsed: any;
  
  try {
    parsed = JSON.parse(data);
  } catch (error) {
    throw new Error('Invalid JSON format');
  }
  
  if (!parsed || typeof parsed !== 'object') {
    throw new Error('Import data must be an object');
  }
  
  if (!parsed.songs || !Array.isArray(parsed.songs)) {
    throw new Error('Import data must contain a songs array');
  }
  
  // Validate songs
  const invalidSongs = parsed.songs.filter((song: any) => !validateSong(song));
  if (invalidSongs.length > 0) {
    throw new Error(`Invalid song data: ${invalidSongs.length} songs have invalid format`);
  }
  
  // Validate setlists if present
  if (parsed.setlists) {
    if (!Array.isArray(parsed.setlists)) {
      throw new Error('Setlists must be an array');
    }
    
    const invalidSetlists = parsed.setlists.filter((setlist: any) => !validateSetlist(setlist));
    if (invalidSetlists.length > 0) {
      throw new Error(`Invalid setlist data: ${invalidSetlists.length} setlists have invalid format`);
    }
  }
  
  return {
    songs: parsed.songs,
    setlists: parsed.setlists || [],
    exportedAt: parsed.exportedAt || new Date().toISOString(),
    version: parsed.version || '1.0'
  };
};